import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, X } from "lucide-react";

interface FilterSection {
  id: string;
  label: string;
  options: { value: string; label: string; count: number }[];
}

interface ContactFilterSidebarProps {
  totalCount: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterSections: FilterSection[];
  filterValues: Record<string, string[]>;
  onFilterChange: (sectionId: string, values: string[]) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export function ContactFilterSidebar({
  totalCount,
  searchValue,
  onSearchChange,
  filterSections,
  filterValues,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
}: ContactFilterSidebarProps) {
  return (
    <div className="w-[240px] border-r p-4 space-y-4 overflow-auto">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{totalCount} contacts</span>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClearFilters} className="h-7 text-xs gap-1">
            <X className="h-3 w-3" /> Clear
          </Button>
        )}
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search contacts..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 h-9"
        />
      </div>
      {filterSections.map((section) => (
        <div key={section.id} className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {section.label}
          </p>
          <div className="space-y-1.5">
            {section.options.map((option) => {
              const isChecked = filterValues[section.id]?.includes(option.value) || false;
              return (
                <label
                  key={option.value}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:text-foreground"
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      const current = filterValues[section.id] || [];
                      const next = checked
                        ? [...current, option.value]
                        : current.filter((v) => v !== option.value);
                      onFilterChange(section.id, next);
                    }}
                  />
                  <span className="truncate flex-1">{option.label}</span>
                  <span className="text-xs text-muted-foreground">{option.count}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
